import React, { useState, useEffect } from 'react'
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router-dom'
import { useAPI } from '../../../Contexts/APIContext'
import { useInView } from 'react-intersection-observer'
import { ButtonOutline } from '../../../Components/GeneralComponents/ButtonOutline/ButtonOutline'
import { BackdropModal } from '../../../Components/SongsComponents/BackdropModal/BackdropModule'
import { Breadcrumbs } from '../../../Components/GeneralComponents/Breadcrumbs/Breadcrumbs'
import { formatStringInNav } from '../../../Helpers/String+Extension'
import { StickyHeader } from '../../../Components/StickyHeader'
import { Wrapper } from '../../../Components/page/Wrapper'
import errorToast from '../../../Components/Toasts/ErrorToast'
import Loader from '../../../Components/GeneralComponents/Loader'
import Prompt from '../../../Components/DashboardComponents/Prompt/Prompt'
import trash from '../../../Components/icons/backdrop_icons/trash_icon.svg'
import classes from './index.module.css'

export function Backdrop() {
  const pageLimit = 20
  const navigate = useNavigate()
  const {
    getBackdropList,
    getUrlNewBackdrop,
    uploadBackdropMedia,
    updateBackdrop,
    deleteBackdrop,
    getBackdrop,
    getSongData,
    updateAlternativeBackdrops,
    fetchAlternativeBackdrops,
  } = useAPI()
  const queryClient = useQueryClient()
  const { id, artist_name } = useParams()
  const [ref, inView] = useInView()

  const [isLoading, setIsLoading] = useState(false)
  const [deleteItem, setDeleteItem] = useState(null)
  const [isOpenModal, setIsOpenModal] = useState(false)

  const [uploadFile, setUploadFile] = useState(null)
  const [isFileUploading, setIsFileUploading] = useState(false)
  const [newBackdrop, setNewBackdrop] = useState(null) // new backdrop f_id and upload url
  const [newBackdropStatus, setNewBackdropStatus] = useState(null)
  const [newMediaStatus, setNewMediaStatus] = useState(false)
  const [selectedBackdrop, setSelectedBackdrop] = useState(null)
  const [initialBackdropSet, setInitialBackdropSet] = useState(false)
  const [videoKey, setVideoKey] = useState(0)
  const [isBackdropUpdated, setIsBackdropUpdated] = useState(false)
  const [song, setSong] = useState(undefined)
  const [showScrollIndicator, setShowScrollIndicator] = useState(false)
  const [showLeftScrollIndicator, setShowLeftScrollIndicator] = useState(false)
  const [showAlternativeScrollIndicator, setShowAlternativeScrollIndicator] = useState(false)
  const [showAlternativeLeftScrollIndicator, setShowAlternativeLeftScrollIndicator] = useState(false)
  const [alternativeBackdrops, setAlternativeBackdrops] = useState([])
  const [initialAlternativeBackdrops, setInitialAlternativeBackdrops] = useState([])
  const [draggedBackdrop, setDraggedBackdrop] = useState(null)
  const [draggedIndex, setDraggedIndex] = useState(null)
  const [dragOverIndex, setDragOverIndex] = useState(null)
  const {
    data,
    isError,
    error,
    isLoading: isLoadingData,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery({
    queryKey: ['backdropList', artist_name, id],
    queryFn: async ({ pageParam = 1 }) => {
      const fetchAllPages = async (currentPage = 1, allBackdrops = []) => {
        const result = await getBackdropList(artist_name, currentPage, pageLimit, id)
        
        const updatedBackdrops = [...allBackdrops, ...result.backdrops]
        
        // If we have more pages to fetch
        if (updatedBackdrops.length < result.backdrops_count) {
          return fetchAllPages(currentPage + 1, updatedBackdrops)
        }
        
        // Return the result with all backdrops
        return {
          ...result,
          backdrops: updatedBackdrops
        }
      }
      
      return fetchAllPages(pageParam)
    },
    getNextPageParam: () => undefined, // Disable pagination since we fetch all at once
  })

  const {
    data: songData,
    isLoading: isSongDataLoading,
    isError: isSongDataError,
  } = useQuery({
    queryKey: ['songData', id],
    queryFn: () => {
      return getSongData(id)
    },
    enabled: !!data,
    refetchInterval:
      isBackdropUpdated && selectedBackdrop?.f_id !== song?.video?.backdrop_f_id
        ? 500
        : false,
  })

  const { isError: isErrorDataNewBackdrop } = useQuery({
    queryKey: ['dataNewBackdropStatus'],
    queryFn: async () => {
      const data = await getBackdrop(newBackdrop.f_id)
      setNewBackdropStatus(data?.status)
      return data
    },
    enabled: newMediaStatus && !!newBackdrop?.f_id,
    staleTime: 0,
    refetchInterval: newBackdropStatus !== 'ready' ? 500 : false,
  })

  const uploadNewBackdrop = async () => {
    try {
      await queryClient.invalidateQueries({
        queryKey: ['backdropList'],
      })
    } catch (e) {
      errorToast(`Error: ${e.status ?? ''} ${e.message ?? ''}`)
    } finally {
      setUploadFile(null)
      setNewBackdrop(null)
      setIsFileUploading(false)
      setNewMediaStatus(false)
    }
  }

  useEffect(() => {
    if (newBackdropStatus === 'ready') {
      uploadNewBackdrop()
    }
  }, [newBackdropStatus])

  useEffect(() => {
    setSong(songData)
    // Set initial selected backdrop to match current default
    if (songData && !initialBackdropSet && data?.pages) {
      if (!songData.video.backdrop_f_id) {
        // If no backdrop is set, select "original"
        setSelectedBackdrop('original')
      } else {
        // Find and select the current backdrop
        for (const page of data.pages) {
          const currentBackdrop = page.backdrops?.find(
            b => b.f_id === songData.video.backdrop_f_id
          )
          if (currentBackdrop) {
            setSelectedBackdrop(currentBackdrop)
            break
          }
        }
      }
      setInitialBackdropSet(true)
    }
  }, [songData, data, initialBackdropSet])

  useEffect(() => {
    // Check if there are more than 7 items (including original)
    let totalItems = 1 // Start with 1 for the original
    if (data?.pages) {
      data.pages.forEach(page => {
        totalItems += page.backdrops?.length || 0
      })
    }
    setShowScrollIndicator(totalItems > 7)
    // Left indicator starts as false since we start at scroll position 0
    setShowLeftScrollIndicator(false)
  }, [data])

  useEffect(() => {
    // Check if alternative backdrops need scroll indicator
    // Show right indicator if there are more than 2.5 items (since max-width shows ~2.5 items)
    setShowAlternativeScrollIndicator(alternativeBackdrops.length > 2)
    // Left indicator starts as false since we start at scroll position 0
    setShowAlternativeLeftScrollIndicator(false)
  }, [alternativeBackdrops])

  const invalidateHandler = async () => {
    await queryClient.invalidateQueries({
      queryKey: ['songData', id],
    })
    await queryClient.invalidateQueries({
      queryKey: ['backdropList'],
    })
    navigate('/song/' + id)
    setIsBackdropUpdated(false)
    setNewBackdrop(null)
    setSelectedBackdrop(null)
    setIsLoading(false)
  }

  useEffect(() => {
    if (
      songData &&
      selectedBackdrop &&
      selectedBackdrop.f_id === songData.video.backdrop_f_id &&
      isBackdropUpdated
    ) {
      invalidateHandler()
    }
  }, [songData, selectedBackdrop, isBackdropUpdated])

  // Fetch alternative backdrops on component mount
  useEffect(() => {
    const loadAlternativeBackdrops = async () => {
      if (id && data) {  // Wait for backdrop list data to be available
        try {
          const response = await fetchAlternativeBackdrops(id)
          
          if (response?.backdrops && Array.isArray(response.backdrops)) {
            // Extract f_ids from the response and find matching backdrops in our loaded data
            const backdropFIDs = response.backdrops.map(b => b.f_id)
            
            // Find the full backdrop objects from our loaded backdrop list
            const allBackdrops = data?.pages?.flatMap(page => page.backdrops) || []
            const matchingBackdrops = allBackdrops.filter(backdrop => 
              backdropFIDs.includes(backdrop.f_id)
            )
            
            setAlternativeBackdrops(matchingBackdrops)
            setInitialAlternativeBackdrops(matchingBackdrops)
          }
        } catch (error) {
          console.error('Failed to fetch alternative backdrops:', error)
        }
      }
    }
    
    loadAlternativeBackdrops()
  }, [id, data])

  useEffect(() => {
    if (isErrorDataNewBackdrop) {
      errorToast('Something went wrong')
    }
  }, [isErrorDataNewBackdrop])

  const getUrlNewBackdropMutation = useMutation({
    mutationFn: ({ backdropType, mediaType }) =>
      getUrlNewBackdrop(data?.pages[0]?.artist?.f_id, backdropType, mediaType),
    onSuccess: async data => {
      setNewBackdrop(data)
      try {
        await uploadBackdropMedia(
          data.upload_url[0],
          uploadFile,
          uploadFile?.type
        )
        setNewMediaStatus(true)
      } catch (e) {
        errorToast(`Error: ${e.status ?? ''} ${e.message ?? ''}`)
        setIsFileUploading(false)
      }
    },
    onError: e => {
      setIsFileUploading(false)
    },
  })

  useEffect(() => {
    if (inView && hasNextPage && data) {
      fetchNextPage()
    }
  }, [inView, hasNextPage, data])

  const handleFileChange = event => {
    setIsFileUploading(true)
    const file = event.target.files[0]
    if (file.type === 'video/mp4') {
      // validation video resolution
      try {
        const video = document.createElement('video')
        video.src = URL.createObjectURL(file)
        video.onloadedmetadata = function () {
          const { videoWidth, videoHeight, duration } = video
          if (videoWidth < 720) {
            errorToast('Video width must be at least 720px')
            setIsFileUploading(false)
            return
          }
          if (videoHeight < 1080) {
            errorToast('Video height must be at least 1080px')
            setIsFileUploading(false)
            return
          }
          if (duration < 3) {
            errorToast('Video must be longer than 3 seconds')
            setIsFileUploading(false)
            return
          }
          if (duration > 15) {
            errorToast('Video must be shorter than 15 seconds')
            setIsFileUploading(false)
            return
          }
          setUploadFile(file)
          getUrlNewBackdropMutation.mutate({
            backdropType: 'video',
            mediaType: 'video/mp4',
          })
        }
        video.onerror = function () {
          setIsFileUploading(false)
          errorToast('Error loading video')
        }
        video.load()
      } catch (e) {
        setIsFileUploading(false)
        errorToast('Error:', e)
      }
    } else if (file.type === 'image/jpeg' || file.type === 'image/png') {
      // validation image resolution
      try {
        const image = new Image()
        image.src = URL.createObjectURL(file)
        image.onload = function () {
          const { width, height } = image
          if (width < 720 || height < 1280) {
            errorToast('Image needs to be at least 720px x 1280px resolution')
            setIsLoading(false)
            return
          }
          setUploadFile(file)
          getUrlNewBackdropMutation.mutate({
            backdropType: 'image',
            mediaType: file.type,
          })
        }

        image.onerror = function () {
          setIsLoading(false)
          errorToast('Error loading image')
        }
        image.decode()
      } catch (e) {
        setIsLoading(false)
        errorToast('Error:', e)
      }
    } else {
      setIsLoading(false)
      errorToast('Only MP4, JPEG, PNG files are allowed')
    }
  }

  const saveHandler = async () => {
    setIsLoading(true)
    setIsOpenModal(false)
    try {
      // Update the default backdrop
      await updateBackdrop(
        id,
        selectedBackdrop === 'original' ? null : selectedBackdrop.f_id
      )

      // Update alternative backdrops - convert to array of f_ids
      const backdropFIDs = alternativeBackdrops
        .filter(backdrop => backdrop !== 'original') // Exclude original if somehow in the list
        .map(backdrop => backdrop.f_id)
      
      await updateAlternativeBackdrops(id, backdropFIDs)

      if (selectedBackdrop?.f_id === song?.video.backdrop_f_id) {
        navigate('/song/' + id)
        setIsBackdropUpdated(false)
        setNewBackdrop(null)
        setSelectedBackdrop(null)
        setIsLoading(false)
      } else {
        setIsBackdropUpdated(true)
      }
    } catch (e) {
      setIsLoading(false)
      errorToast(`Error ${e.status ?? ''} ${e.message ?? ''}`)
    }
  }

  const deleteHandler = async deleteItem => {
    setIsLoading(true)
    setDeleteItem(null)
    try {
      await deleteBackdrop(deleteItem.f_id)
      await queryClient.invalidateQueries({
        queryKey: ['backdropList'],
      })
    } catch (e) {
      errorToast(`Error: ${e.status ?? ''} ${e.message ?? ''}`)
    }
    setIsLoading(false)
  }

  const handleVideoChange = backdrop => {
    setSelectedBackdrop(backdrop)
    setVideoKey(prevKey => prevKey + 1)
  }

  const handleRevertOriginalBackdrop = () => {
    setSelectedBackdrop('original')
    setVideoKey(prevKey => prevKey + 1)
  }

  const handleDoubleClickBackdrop = backdrop => {
    // If double-clicking the currently selected backdrop, deselect it
    if (selectedBackdrop === backdrop || 
        (selectedBackdrop === 'original' && backdrop === 'original') ||
        (selectedBackdrop && backdrop && selectedBackdrop.f_id === backdrop.f_id)) {
      setSelectedBackdrop(null)
      setVideoKey(prevKey => prevKey + 1)
    }
  }

  const handleSingleClickBackdrop = (backdrop) => {
    if (!selectedBackdrop) {
      // When default is empty, only set as default (don't add to alternatives)
      handleVideoChange(backdrop)
    } else if (selectedBackdrop !== backdrop && 
               !(selectedBackdrop && backdrop && selectedBackdrop.f_id === backdrop.f_id)) {
      // When default is set and clicking a different backdrop, only toggle in alternatives
      handleBackdropClick(backdrop)
    }
    // If clicking the currently selected backdrop, do nothing
  }

  const handleSingleClickOriginal = () => {
    if (!selectedBackdrop) {
      // When default is empty, only set as default (don't add to alternatives)
      handleRevertOriginalBackdrop()
    } else if (selectedBackdrop !== 'original') {
      // When default is set and clicking original (if not already selected), only toggle in alternatives
      handleBackdropClick('original')
    }
    // If original is already selected as default, do nothing
  }

  const handleBackdropClick = backdrop => {
    // Prevent adding original visual to alternatives
    if (backdrop === 'original') {
      errorToast('Original Song Visual can only be used as the default backdrop')
      return
    }
    
    // Don't allow adding the currently selected default backdrop to alternatives
    if (selectedBackdrop === backdrop || 
        (selectedBackdrop && backdrop && selectedBackdrop.f_id === backdrop.f_id)) {
      return
    }
    
    // Check if backdrop is already in alternative list
    const isInAlternative = alternativeBackdrops.some(b => 
      (typeof b !== 'string' && typeof backdrop !== 'string' && b.f_id === backdrop.f_id)
    )
    
    if (isInAlternative) {
      // Remove from alternative list
      setAlternativeBackdrops(prev => prev.filter(b => 
        (typeof b !== 'string' && typeof backdrop !== 'string' && b.f_id !== backdrop.f_id)
      ))
    } else {
      // Add to alternative list if under limit
      if (alternativeBackdrops.length < 10) {
        setAlternativeBackdrops(prev => [...prev, backdrop])
      }
    }
  }

  const removeFromAlternative = backdrop => {
    setAlternativeBackdrops(prev => {
      const filtered = prev.filter(b => 
        (typeof b === 'string' && b !== backdrop) ||
        (typeof b !== 'string' && typeof backdrop !== 'string' && b.f_id !== backdrop.f_id)
      )
      return filtered
    })
  }

  const handleDragStart = (e, backdrop) => {
    setDraggedBackdrop(backdrop)
    e.dataTransfer.effectAllowed = 'copy'
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'copy'
  }

  const handleDrop = (e) => {
    e.preventDefault()
    if (draggedBackdrop) {
      setSelectedBackdrop(draggedBackdrop)
      setVideoKey(prev => prev + 1) // Force video refresh
      
      // Remove from alternative backdrops if it's in there (skip for original video)
      if (draggedBackdrop !== 'original') {
        setAlternativeBackdrops(prev => prev.filter(backdrop => 
          typeof backdrop === 'string' || backdrop.f_id !== draggedBackdrop.f_id
        ))
      }
      
      setDraggedBackdrop(null)
    }
  }

  const handleDragEnd = () => {
    setDraggedBackdrop(null)
    setDraggedIndex(null)
    setDragOverIndex(null)
  }

  const handleAlternativeDragOver = (e) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'copy'
  }

  const handleAlternativeItemDragStart = (e, backdrop, index) => {
    setDraggedBackdrop(backdrop)
    setDraggedIndex(index)
    e.dataTransfer.effectAllowed = 'move'
  }

  const handleAlternativeItemDragOver = (e, index) => {
    e.preventDefault()
    e.stopPropagation()
    if (draggedIndex !== null) {
      setDragOverIndex(index)
      e.dataTransfer.dropEffect = 'move'
    }
  }

  const handleAlternativeItemDrop = (e, index) => {
    e.preventDefault()
    e.stopPropagation()
    if (draggedIndex !== null && draggedIndex !== index) {
      const newBackdrops = [...alternativeBackdrops]
      const draggedItem = newBackdrops[draggedIndex]
      newBackdrops.splice(draggedIndex, 1)
      newBackdrops.splice(index, 0, draggedItem)
      setAlternativeBackdrops(newBackdrops)
    }
    setDraggedBackdrop(null)
    setDraggedIndex(null)
    setDragOverIndex(null)
  }

  const handleAlternativeScroll = (e) => {
    const { scrollLeft, scrollWidth, clientWidth } = e.target
    setShowAlternativeLeftScrollIndicator(scrollLeft > 0)
    setShowAlternativeScrollIndicator(scrollLeft < scrollWidth - clientWidth)
  }

  const handleLibraryScroll = (e) => {
    const { scrollLeft, scrollWidth, clientWidth } = e.target
    setShowLeftScrollIndicator(scrollLeft > 0)
    setShowScrollIndicator(scrollLeft < scrollWidth - clientWidth)
  }

  const handleAlternativeDrop = (e) => {
    e.preventDefault()
    if (draggedBackdrop) {
      // Handle reordering if dragging from within alternative list
      if (draggedIndex !== null && dragOverIndex !== null && draggedIndex !== dragOverIndex) {
        const newBackdrops = [...alternativeBackdrops]
        const draggedItem = newBackdrops[draggedIndex]
        newBackdrops.splice(draggedIndex, 1)
        newBackdrops.splice(dragOverIndex, 0, draggedItem)
        setAlternativeBackdrops(newBackdrops)
        setDraggedBackdrop(null)
        setDraggedIndex(null)
        setDragOverIndex(null)
        return
      }
      
      // Prevent adding original visual to alternatives
      if (draggedBackdrop === 'original') {
        errorToast('Original Song Visual can only be used as the default backdrop')
        setDraggedBackdrop(null)
        return
      }
      
      // If dragging the current default backdrop, clear the default first
      if (selectedBackdrop) {
        const isCurrentDefault = 
          (selectedBackdrop === draggedBackdrop) ||
          (selectedBackdrop && draggedBackdrop && selectedBackdrop.f_id === draggedBackdrop.f_id)
        
        if (isCurrentDefault) {
          setSelectedBackdrop(null)
        }
      }
      
      // Check if backdrop is already in alternative list
      const isInAlternative = alternativeBackdrops.some(b => 
        (typeof b !== 'string' && typeof draggedBackdrop !== 'string' && b.f_id === draggedBackdrop.f_id)
      )
      
      if (isInAlternative) {
        // Remove from alternative list
        setAlternativeBackdrops(prev => prev.filter(b => 
          (typeof b !== 'string' && typeof draggedBackdrop !== 'string' && b.f_id !== draggedBackdrop.f_id)
        ))
      } else {
        // Add to alternative list if under limit
        if (alternativeBackdrops.length < 10) {
          setAlternativeBackdrops(prev => [...prev, draggedBackdrop])
        }
      }
      
      setDraggedBackdrop(null)
    }
  }

  const isInAlternativeBackdrops = (backdrop) => {
    // Original video can never be in alternatives
    if (backdrop === 'original') return false
    
    return alternativeBackdrops.some(alt => {
      if (typeof alt !== 'string' && typeof backdrop !== 'string' && alt.f_id === backdrop.f_id) return true
      return false
    })
  }

  const hasAlternativeBackdropsChanged = () => {
    // Check if lengths are different
    if (alternativeBackdrops.length !== initialAlternativeBackdrops.length) {
      return true
    }
    
    // Check if order or content has changed (don't sort - preserve order)
    const currentFIDs = alternativeBackdrops.map(b => b.f_id)
    const initialFIDs = initialAlternativeBackdrops.map(b => b.f_id)
    
    // Compare arrays element by element to detect order changes
    for (let i = 0; i < currentFIDs.length; i++) {
      if (currentFIDs[i] !== initialFIDs[i]) {
        return true
      }
    }
    
    return false
  }

  return (
    <>
      {deleteItem && (
        <Prompt
          title={'Confirm Deletion of Backdrop'}
          subtitle={
            'Are you sure you want to delete this backdrop video? This action is permanent and cannot be undone.'
          }
          primaryAction={'CANCEL'}
          onPrimaryClick={() => {
            setDeleteItem(null)
          }}
          secondaryAction={'DELETE'}
          onSecondaryClick={() => {
            deleteHandler(deleteItem)
          }}
          isSecondaryActionDestructive={true}
          onCloseClick={() => {
            setDeleteItem(null)
          }}
        />
      )}

      <BackdropModal
        isOpenModal={isOpenModal}
        setIsOpenModal={setIsOpenModal}
        onSubmit={saveHandler}
      />
      <Wrapper scrollY>
        {!isLoadingData && (
          <StickyHeader className={classes.stickyHeader}>
            <Breadcrumbs
              items={
                !data
                  ? [
                      {
                        text: 'All Songs',
                        onClick: () => {
                          navigate('/songs/all')
                        },
                      },
                    ]
                  : [
                      {
                        text: 'All Songs',
                        onClick: () => {
                          navigate('/songs/all')
                        },
                      },
                      {
                        text: formatStringInNav(data?.pages[0]?.artist?.name),
                        onClick: () => {
                          navigate(
                            '/artist/' + data?.pages[0]?.artist?.artist_name
                          )
                        },
                      },
                      {
                        text: 'Songs',
                        onClick: () => {
                          navigate(
                            '/songs/artist/' +
                              data?.pages[0]?.artist?.artist_name
                          )
                        },
                      },
                      {
                        text: formatStringInNav(data?.pages[0]?.song?.name),
                        onClick: () => {
                          navigate('/song/' + data?.pages[0]?.song_f_id)
                        },
                      },
                      {
                        text: 'Manage Backdrops',
                        onClick: () => {},
                      },
                    ]
              }
            />
            <div className={classes.header}>
              <div className={classes.headerTitleWrapper}>
                <span className={classes.headerTitle}>Manage Backdrops</span>
                <span className={classes.headerSubtitle}>
                  Update the Backdrop options for {data?.pages[0]?.song?.name || ''}.
                </span>
              </div>
              <div className={classes.headerButtons}>
                <ButtonOutline
                  disable={
                    isLoading ||
                    (!selectedBackdrop && !hasAlternativeBackdropsChanged()) ||
                    (!songData?.video?.backdrop_f_id &&
                      selectedBackdrop === 'original' &&
                      !hasAlternativeBackdropsChanged())
                  }
                  onClick={() => {
                    setIsOpenModal(true)
                  }}
                  fontFamily="Noto Sans"
                  fontWeight={700}
                  text={'CONFIRM SELECTION'}
                  color={'pink'}
                  className={classes.confirmButton}
                />
              </div>
            </div>
          </StickyHeader>
        )}
        {data &&
          songData &&
          !isError &&
          !isLoadingData &&
          !isSongDataLoading &&
          !isLoading && (
            <>
              <div className={classes.contentWrapper}>
                <div className={classes.uploadContainer}>
                  <div className={classes.contentTitleContainer}>
                    <span className={classes.contentTitle}>
                      Default Backdrop
                    </span>
                    <div className={classes.tooltipWrapper}>
                      <svg 
                        className={classes.infoIcon}
                        width="16" 
                        height="16" 
                        viewBox="0 0 16 16" 
                        fill="none" 
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <circle cx="8" cy="8" r="7" stroke="#999" strokeWidth="1.5"/>
                        <path d="M8 7V11" stroke="#999" strokeWidth="1.5" strokeLinecap="round"/>
                        <circle cx="8" cy="5" r="0.5" fill="#999"/>
                      </svg>
                      <div className={classes.tooltip}>
                        <p>Dimensions: 720px x 1280px</p>
                        <p>Length: 3-15 seconds (can trim)</p>
                      </div>
                    </div>
                  </div>
                  <span className={classes.contentSubtitle}>
                    This will be the default visual for every Feels made with this song. Required.
                  </span>
                  {!selectedBackdrop ? (
                    <div 
                      className={classes.defaultEmptyState}
                      onDragOver={handleDragOver}
                      onDrop={handleDrop}
                    >
                      <p>No backdrop selected</p>
                      <span>Drag a backdrop here or click to select. A default backdrop is required to proceed.</span>
                    </div>
                  ) : selectedBackdrop === 'original' ? (
                    <div 
                      className={classes.defaultBackdropContainer}
                      onDragOver={handleDragOver}
                      onDrop={handleDrop}
                    >
                      <video
                        className={classes.uploadVideo}
                        controls
                        autoPlay
                        loop
                        muted
                        style={{ objectFit: 'cover' }}
                        key={videoKey}
                      >
                        <source
                          src={
                            songData?.video?.source_video?.url ||
                            songData?.video?.video?.url
                          }
                          type="video/mp4"
                        />
                        Your browser does not support the video tag.
                      </video>
                      <button
                        className={classes.defaultRemoveButton}
                        onClick={() => setSelectedBackdrop(null)}
                        title="Remove default backdrop"
                      >
                        ×
                      </button>
                    </div>
                  ) : selectedBackdrop?.media_type === 'image/jpeg' ||
                    selectedBackdrop?.media_type === 'image/png' ? (
                    <div 
                      className={classes.defaultBackdropContainer}
                      onDragOver={handleDragOver}
                      onDrop={handleDrop}
                    >
                      <img
                        src={selectedBackdrop?.image?.url}
                        alt="backdrop"
                        className={classes.uploadVideo}
                      />
                      <button
                        className={classes.defaultRemoveButton}
                        onClick={() => setSelectedBackdrop(null)}
                        title="Remove default backdrop"
                      >
                        ×
                      </button>
                    </div>
                  ) : (
                    <div 
                      className={classes.defaultBackdropContainer}
                      onDragOver={handleDragOver}
                      onDrop={handleDrop}
                    >
                      <video
                        className={classes.uploadVideo}
                        controls
                        autoPlay
                        loop
                        muted
                        style={{ objectFit: 'cover' }}
                        key={videoKey}
                      >
                        <source
                          src={selectedBackdrop?.video?.url}
                          type="video/mp4"
                        />
                        Your browser does not support the video tag.
                      </video>
                      <button
                        className={classes.defaultRemoveButton}
                        onClick={() => setSelectedBackdrop(null)}
                        title="Remove default backdrop"
                      >
                        ×
                      </button>
                    </div>
                  )}
                </div>

                <div className={classes.alternativeContainer}>
                  <div className={classes.contentTitleContainer}>
                    <span className={classes.contentTitle}>
                      Alternative Backdrops
                    </span>
                  </div>
                  <span className={classes.alternativeSubtitle}>
                    Any backdrops placed here will be alternative backdrop options for users to pick between.  These will be live in the app, presented as &quot;Artist Picks&quot; for this song.
                  </span>
                  {alternativeBackdrops.length === 0 ? (
                    <div 
                      className={classes.alternativePlaceholder}
                      onDragOver={handleAlternativeDragOver}
                      onDrop={handleAlternativeDrop}
                    >
                      <p>Click or Drag a backdrop to place it here</p>
                    </div>
                  ) : (
                    <div className={classes.libraryContainer} style={{ position: 'relative' }}>
                      <div 
                        className={classes.alternativeBackdropsGrid}
                        onDragOver={handleAlternativeDragOver}
                        onDrop={handleAlternativeDrop}
                        onScroll={handleAlternativeScroll}
                      >
                        {alternativeBackdrops.map((backdrop, index) => (
                          <div 
                            key={backdrop === 'original' ? 'original' : backdrop.f_id} 
                            className={classes.alternativeBackdropItem}
                            draggable="true"
                            onDragStart={(e) => handleAlternativeItemDragStart(e, backdrop, index)}
                            onDragOver={(e) => handleAlternativeItemDragOver(e, index)}
                            onDrop={(e) => handleAlternativeItemDrop(e, index)}
                            onDragEnd={handleDragEnd}
                            onMouseEnter={(e) => {
                              const video = e.currentTarget.querySelector('video')
                              if (video) {
                                video.play()
                                // Pause default backdrop video
                                const defaultVideo = document.querySelector(`[class*="defaultBackdropContainer"] video`)
                                if (defaultVideo) defaultVideo.pause()
                              }
                            }}
                            onMouseLeave={(e) => {
                              const video = e.currentTarget.querySelector('video')
                              if (video) {
                                video.pause()
                                video.currentTime = 0
                                // Resume default backdrop video
                                const defaultVideo = document.querySelector(`[class*="defaultBackdropContainer"] video`)
                                if (defaultVideo) defaultVideo.play()
                              }
                            }}
                          >
                            {backdrop === 'original' ? (
                              <video
                                className={classes.alternativeVideo}
                                autoPlay={false}
                                muted={true}
                                loop={true}
                              >
                                <source
                                  src={
                                    songData.video.backdrop_f_id
                                      ? songData.video.source_video.url
                                      : songData.video.video.url
                                  }
                                  type="video/mp4"
                                />
                              </video>
                            ) : backdrop.backdrop_type === 'image' ? (
                              <img
                                src={backdrop.image.url}
                                className={classes.alternativeImage}
                                alt="alternative backdrop"
                              />
                            ) : (
                              <video
                                className={classes.alternativeVideo}
                                autoPlay={false}
                                muted={true}
                                loop={true}
                              >
                                <source
                                  src={backdrop.video.url}
                                  type="video/mp4"
                                />
                              </video>
                            )}
                            <button
                              className={classes.removeButton}
                              onClick={() => removeFromAlternative(backdrop)}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                        {alternativeBackdrops.length < 10 && (
                          <div className={classes.alternativeBackdropItem}>
                            <div className={classes.alternativePlaceholder}>
                              <p>Click or Drag a backdrop to place it here</p>
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {/* Left scroll indicator */}
                      {showAlternativeLeftScrollIndicator && (
                        <div style={{ 
                          position: 'absolute', 
                          left: '8px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          zIndex: 10,
                          backgroundColor: 'rgba(0, 0, 0, 0.7)',
                          borderRadius: '50%',
                          width: '32px',
                          height: '32px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          opacity: 0.8
                        }}>
                          <svg 
                            width="20" 
                            height="20" 
                            viewBox="0 0 24 24" 
                            fill="none" 
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path 
                              d="M15 18L9 12L15 6" 
                              stroke="#fff" 
                              strokeWidth="2" 
                              strokeLinecap="round" 
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                      )}
                      
                      {/* Right scroll indicator */}
                      {showAlternativeScrollIndicator && (
                        <div style={{ 
                          position: 'absolute', 
                          right: '8px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          zIndex: 10,
                          backgroundColor: 'rgba(0, 0, 0, 0.7)',
                          borderRadius: '50%',
                          width: '32px',
                          height: '32px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          opacity: 0.8
                        }}>
                          <svg 
                            width="20" 
                            height="20" 
                            viewBox="0 0 24 24" 
                            fill="none" 
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path 
                              d="M9 18L15 12L9 6" 
                              stroke="#fff" 
                              strokeWidth="2" 
                              strokeLinecap="round" 
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className={classes.yourContentContainer}>
                  <div className={classes.contentHeader}>
                    <div className={classes.contentHeaderText}>
                      <div className={classes.contentTitleRow}>
                        <span className={classes.contentTitle}>
                          Backdrop Library
                        </span>
                        <label
                          htmlFor="fileInput"
                          className={classes.uploadVideoButton}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="12"
                            height="12"
                            viewBox="0 0 12 12"
                            fill="none"
                          >
                            <path
                              d="M5.25 9V2.8875L3.3 4.8375L2.25 3.75L6 0L9.75 3.75L8.7 4.8375L6.75 2.8875V9H5.25ZM1.5 12C1.0875 12 0.7345 11.8533 0.441 11.5597C0.1475 11.2662 0.0005 10.913 0 10.5V8.25H1.5V10.5H10.5V8.25H12V10.5C12 10.9125 11.8533 11.2657 11.5597 11.5597C11.2662 11.8538 10.913 12.0005 10.5 12H1.5Z"
                              fill="#FF00FF"
                            />
                          </svg>
                          <span>UPLOAD VIDEO</span>
                          <input
                            type="file"
                            id="fileInput"
                            accept=".jpg, .jpeg, .png, .mp4"
                            onChange={handleFileChange}
                            style={{ display: 'none' }}
                          />
                        </label>
                      </div>
                      <span className={classes.librarySubtext}>
                        Click to select a backdrop for this song. Double-click to deselect
                      </span>
                    </div>
                  </div>

                  <div className={classes.libraryContainer} style={{ position: 'relative' }}>
                    <div 
                      className={classes.yourContentWrapper}
                      onScroll={handleLibraryScroll}
                    >
                    <div className={classes.originalTrack}>
                      <div 
                        className={classes.yourContentItemWrapper}
                        onClick={handleSingleClickOriginal}
                        onDoubleClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          handleDoubleClickBackdrop('original')
                        }}
                        draggable="true"
                        onDragStart={(e) => handleDragStart(e, 'original')}
                        onDragEnd={handleDragEnd}
                      >
                        <video
                          className={classes.yourContentVideo}
                          autoPlay={false}
                          muted={true}
                          style={{ pointerEvents: 'none' }}
                        >
                          <source
                            src={
                              songData.video.backdrop_f_id
                                ? songData.video.source_video.url
                                : songData.video.video.url
                            }
                            type="video/mp4"
                          />
                          Your browser does not support the video tag.
                        </video>
                        <div className={classes.originalLabel}>
                          Original Song Visual
                        </div>
                        {selectedBackdrop === 'original' ? (
                          <div className={classes.selectedBackdrop}>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="56"
                              height="56"
                              viewBox="0 0 56 56"
                              fill="none"
                            >
                              <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M46.0303 13.8581C47.3972 15.225 47.3972 17.441 46.0303 18.8079L24.6414 40.1968C23.9851 40.8532 23.0948 41.2219 22.1666 41.2219C21.2383 41.2219 20.3481 40.8532 19.6917 40.1968L9.96946 30.4746C8.60263 29.1077 8.60263 26.8916 9.96946 25.5248C11.3363 24.158 13.5524 24.158 14.9192 25.5248L22.1666 32.7722L41.0806 13.8581C42.4474 12.4913 44.6635 12.4913 46.0303 13.8581Z"
                                fill="white"
                              />
                            </svg>
                          </div>
                        ) : null}
                      </div>
                    </div>

                    {isFileUploading ? (
                      <div className={classes.yourContentItemLoader}>
                        <Loader isLoading={isFileUploading} />
                      </div>
                    ) : null}

                    {data.pages.map((page, pageIndex) => {
                      const isLastPage = pageIndex === data.pages.length - 1
                      const beforeLastIndex = page.backdrops.length - 2
                      return (
                        <React.Fragment key={pageIndex}>
                          {page.backdrops.map((backdrop, index) => {
                            const isBeforeLast =
                              isLastPage && index === beforeLastIndex
                            return (
                              <div
                                key={backdrop?.f_id}
                                ref={isBeforeLast ? ref : null}
                              >
                                {backdrop.backdrop_type === 'image' && (
                                  <div
                                    className={classes.yourContentItemWrapper}
                                    onClick={() => handleSingleClickBackdrop(backdrop)}
                                    onDoubleClick={(e) => {
                                      e.preventDefault()
                                      e.stopPropagation()
                                      handleDoubleClickBackdrop(backdrop)
                                    }}
                                    draggable="true"
                                    onDragStart={(e) => handleDragStart(e, backdrop)}
                                    onDragEnd={handleDragEnd}
                                  >
                                    <img
                                      src={backdrop.image.url}
                                      className={classes.yourContentImage}
                                      alt="content image"
                                      style={{ pointerEvents: 'none' }}
                                    />

                                    {backdrop.f_id !==
                                      songData?.video.backdrop_f_id &&
                                    backdrop.f_id !== selectedBackdrop?.f_id &&
                                    !isInAlternativeBackdrops(backdrop) ? (
                                      <img
                                        src={trash}
                                        className={classes.trashIcon}
                                        onClick={() => {
                                          setDeleteItem(backdrop)
                                        }}
                                        alt="trash icon"
                                      />
                                    ) : null}
                                    {(backdrop.f_id === selectedBackdrop?.f_id || isInAlternativeBackdrops(backdrop)) ? (
                                      <div className={classes.selectedBackdrop}>
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          width="56"
                                          height="56"
                                          viewBox="0 0 56 56"
                                          fill="none"
                                        >
                                          <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M46.0303 13.8581C47.3972 15.225 47.3972 17.441 46.0303 18.8079L24.6414 40.1968C23.9851 40.8532 23.0948 41.2219 22.1666 41.2219C21.2383 41.2219 20.3481 40.8532 19.6917 40.1968L9.96946 30.4746C8.60263 29.1077 8.60263 26.8916 9.96946 25.5248C11.3363 24.158 13.5524 24.158 14.9192 25.5248L22.1666 32.7722L41.0806 13.8581C42.4474 12.4913 44.6635 12.4913 46.0303 13.8581Z"
                                            fill="white"
                                          />
                                        </svg>
                                      </div>
                                    ) : null}
                                  </div>
                                )}
                                {backdrop.backdrop_type === 'video' && (
                                  <div
                                    className={classes.yourContentItemWrapper}
                                    onClick={() => handleSingleClickBackdrop(backdrop)}
                                    onDoubleClick={(e) => {
                                      e.preventDefault()
                                      e.stopPropagation()
                                      handleDoubleClickBackdrop(backdrop)
                                    }}
                                    draggable="true"
                                    onDragStart={(e) => handleDragStart(e, backdrop)}
                                    onDragEnd={handleDragEnd}
                                  >
                                    <video
                                      className={classes.yourContentVideo}
                                      autoPlay={false}
                                      muted={true}
                                      style={{ pointerEvents: 'none' }}
                                    >
                                      <source
                                        src={backdrop.video.url}
                                        type="video/mp4"
                                      />
                                      Your browser does not support the video
                                      tag.
                                    </video>
                                    {backdrop.f_id !==
                                      songData?.video.backdrop_f_id &&
                                    backdrop.f_id !== selectedBackdrop?.f_id &&
                                    !isInAlternativeBackdrops(backdrop) ? (
                                      <img
                                        src={trash}
                                        className={classes.trashIcon}
                                        onClick={() => {
                                          setDeleteItem(backdrop)
                                        }}
                                        alt="trash icon"
                                      />
                                    ) : null}
                                    {(backdrop.f_id === selectedBackdrop?.f_id || isInAlternativeBackdrops(backdrop)) ? (
                                      <div className={classes.selectedBackdrop}>
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          width="56"
                                          height="56"
                                          viewBox="0 0 56 56"
                                          fill="none"
                                        >
                                          <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M46.0303 13.8581C47.3972 15.225 47.3972 17.441 46.0303 18.8079L24.6414 40.1968C23.9851 40.8532 23.0948 41.2219 22.1666 41.2219C21.2383 41.2219 20.3481 40.8532 19.6917 40.1968L9.96946 30.4746C8.60263 29.1077 8.60263 26.8916 9.96946 25.5248C11.3363 24.158 13.5524 24.158 14.9192 25.5248L22.1666 32.7722L41.0806 13.8581C42.4474 12.4913 44.6635 12.4913 46.0303 13.8581Z"
                                            fill="white"
                                          />
                                        </svg>
                                      </div>
                                    ) : null}
                                  </div>
                                )}
                              </div>
                            )
                          })}
                        </React.Fragment>
                      )
                    })}
                  </div>
                  
                  {/* Left scroll indicator */}
                  {showLeftScrollIndicator && (
                    <div style={{ 
                      position: 'absolute', 
                      left: '8px',
                      top: '45%',
                      transform: 'translateY(-50%)',
                      zIndex: 10,
                      backgroundColor: 'rgba(0, 0, 0, 0.7)',
                      borderRadius: '50%',
                      width: '32px',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      opacity: 0.8
                    }}>
                      <svg 
                        width="20" 
                        height="20" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path 
                          d="M15 18L9 12L15 6" 
                          stroke="#fff" 
                          strokeWidth="2" 
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                  )}
                  
                  {/* Right scroll indicator */}
                  {showScrollIndicator && (
                    <div style={{ 
                      position: 'absolute', 
                      right: '8px',
                      top: '45%',
                      transform: 'translateY(-50%)',
                      zIndex: 10,
                      backgroundColor: 'rgba(0, 0, 0, 0.7)',
                      borderRadius: '50%',
                      width: '32px',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      opacity: 0.8
                    }}>
                      <svg 
                        width="20" 
                        height="20" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path 
                          d="M9 18L15 12L9 6" 
                          stroke="#fff" 
                          strokeWidth="2" 
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        {(isLoadingData || isLoading || isSongDataLoading) && (
          <div className={classes.loader}>
            <Loader
              isLoading={isLoadingData || isLoading || isSongDataLoading}
            />
          </div>
        )}
      </Wrapper>

      {isError && (
        <h1
          style={{ color: 'white', margin: 0 }}
        >{`Failed to fetch data with code: ${
          error?.status ? error.status : 'unknown'
        }`}</h1>
      )}
    </>
  )
}
